import { Component, OnInit, signal, inject, ViewChild, ViewContainerRef, ComponentRef, computed } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { TableModule } from 'primeng/table';
import { ClientService } from '../../../service/client.service';
import { Client, ClientDetails } from '../../models/client.model';
import { PageLayoutComponent } from '../../shared/page-layout/page-layout';
import { FormsModule } from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { Button } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { AddClientDialogComponent } from './add-client-dialog/add-client-dialog.component';
import { StateContainerComponent, ViewState } from '../../shared/state-container/state-container.component';

@Component({
  selector: 'app-app-registration',
  standalone: true,
  imports: [
    CommonModule, 
    TableModule, 
    DatePipe, 
    PageLayoutComponent, 
    FormsModule, 
    InputText, 
    IconField, 
    InputIcon, 
    Button, 
    RouterLink,
    ToastModule,
    StateContainerComponent
  ],
  providers: [MessageService],
  templateUrl: './app-registration.html',
})
export class AppRegistration implements OnInit {
  @ViewChild('dialogContainer', { read: ViewContainerRef }) dialogContainer!: ViewContainerRef;

  private clientService = inject(ClientService);
  private messageService = inject(MessageService);

  clients = signal<Client[]>([]);
  loading = signal(false);
  error = signal<any>(null);
  filteredClients = signal<Client[]>([]);
  searchTerm = '';
  
  private dialogRef: ComponentRef<AddClientDialogComponent> | null = null;

  // Computed state for StateContainer
  readonly viewState = computed<ViewState>(() => {
    if (this.loading()) return 'loading';
    if (this.error()) return 'error';
    if (this.filteredClients().length === 0) return 'empty';
    return 'content';
  });

  readonly showClearFilters = computed(() => {
    return !!this.searchTerm;
  });

  readonly emptyStateMessage = computed(() => {
    if (this.searchTerm) {
      return 'No clients match your current search criteria. Try adjusting your search.';
    }
    return 'Create your first client to start managing your applications.';
  });

  readonly errorMessage = computed(() => {
    const err = this.error();
    if (typeof err === 'string') return err;
    if (err?.message) return err.message;
    return 'Failed to load clients. Please try again.';
  });

  ngOnInit() {
    this.loadClients();
  }

  loadClients() {
    this.loading.set(true);
    this.error.set(null);
    this.clientService.getClients().subscribe({
      next: (res: any) => {
        let clientList: Client[] = [];
        
        if (Array.isArray(res)) {
          clientList = res;
        } else if (res.content && Array.isArray(res.content)) {
          clientList = res.content;
        } else {
          clientList = res || [];
        }
        
        this.clients.set(clientList);
        this.filteredClients.set(clientList);
        this.loading.set(false);
      },
      error: (error) => {
        this.error.set(error);
        this.loading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load clients'
        });
      }
    });
  }

  retryLoad() {
    this.loadClients();
  }

  clearFilters() {
    this.searchTerm = '';
    this.filteredClients.set(this.clients());
  }

  getInitials(clientId: string): string {
    if (!clientId) return '';
    return clientId
      .split(/[\s-_]+/)
      .map(word => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  }

  onSearch() {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      this.filteredClients.set(this.clients());
      return;
    }
    this.filteredClients.set(
      this.clients().filter(client =>
        client.clientId.toLowerCase().includes(term)
      )
    );
  }

  openAddClientDialog() {
    this.dialogRef = this.dialogContainer.createComponent(AddClientDialogComponent);
    
    this.dialogRef.instance.clientAdded.subscribe((newClient: ClientDetails) => {
      this.handleClientAdded(newClient);
    });
    
    this.dialogRef.instance.dialogClose.subscribe(() => {
      this.destroyDialog();
    });
  }

  private handleClientAdded(newClient: ClientDetails) {
    const currentClients = this.clients();
    const newClientListItem: Client = {
      id: newClient.id,
      clientId: newClient.clientId,
      createdDate: newClient.createdDate
    };
    
    this.clients.set([newClientListItem, ...currentClients]);
    this.filteredClients.set([newClientListItem, ...currentClients]);
    
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: `Client "${newClient.clientName}" created successfully`
    });
    
    this.destroyDialog();
  }

  private destroyDialog() {
    if (this.dialogRef) {
      this.dialogRef.destroy();
      this.dialogRef = null;
    }
  }
}